import { createSelector } from '@ngrx/store';

import { ActivityByDate, ActivityByServer } from '@apps/bckp/domain/models';
import { BckpConstants } from '@apps/bckp/bckp.constants';
import { filterQuickSearchWithTags } from '@common/services/utils.service';
import { StatusesMetadata } from '@common/models';
import { jobsAdapter } from '@apps/bckp/store/jobs/jobs.reducer';
import { ActivityByServerDomain } from '@apps/bckp/domain/models/activity';
import { getFilterTags } from '@apps/root/store';
import { FilterTag } from '@apps/cmdb/models';

import * as fromServersReducer from '../servers.reducer';
import * as fromServersHeatState from './heat.state';
import { getBckpServersState } from '../bckp-servers.selectors';
import { IServersHeatState } from './heat.state';
import { BckpHeatConstants } from './bckp-heat.constants';

// #region Servers Heat
const getBckpServersHeatState = createSelector(
    getBckpServersState,
    (state: fromServersReducer.IBckpServersState): fromServersHeatState.IBckpServersHeatState =>
        state[fromServersHeatState.BckpServersHeatFeatureKey]
);
const getBckpServersHeatCollection = createSelector(
    getBckpServersHeatState,
    (state): IServersHeatState =>
        state.servers
);
export const {
    selectAll: getAllServersHeat
} = fromServersHeatState.serversHeatAdapter.getSelectors(getBckpServersHeatCollection);
export const getBckpServersHeatQueryString = createSelector(
    getBckpServersHeatState,
    (state): string =>
        state.oData.qs
);
export const getBckpServersHeatIsDataValid = createSelector(
    getBckpServersHeatState,
    (state): boolean =>
        StatusesMetadata.isDataValid(state.expirationDate)
);
export const getBckpServersHeatEmptyStatus = createSelector(
    getBckpServersHeatState,
    (state) =>
        state.isEmpty
);
export const getBckpServersHeatLoadedStatus = createSelector(
    getBckpServersHeatState,
    (state) =>
        state.isLoaded
);
export const getBckpServersHeatLoadingStatus = createSelector(
    getBckpServersHeatState,
    (state) =>
        state.isLoading
);
export const getBckpServersHeatOupsStatus = createSelector(
    getBckpServersHeatState,
    (state) =>
        state.isOups
);
export const getBckpServersHeatFilter = createSelector(
    getBckpServersHeatState,
    (state) =>
        state.oData.filter
);
export const getBckpServersHeatIssuesOnlyState = createSelector(
    getBckpServersHeatState,
    (state): boolean =>
        state.issuesOnly
);
const getQuickSearchedServersHeat = createSelector(
    getBckpServersHeatState,
    getAllServersHeat,
    getFilterTags,
    (state: fromServersHeatState.IBckpServersHeatState, activityByServers: ActivityByServer[], filterTags: FilterTag[]): ActivityByServer[] =>
        filterQuickSearchWithTags(activityByServers, state.qsWord, BckpConstants.serversHeatQSFields, filterTags)
);
export const getServersHeatIssuesOnly = () => createSelector(
    getBckpServersHeatState,
    getQuickSearchedServersHeat,
    (state: fromServersHeatState.IBckpServersHeatState, activityByServers: ActivityByServer[]): ActivityByServer[] =>
    {
        return state.issuesOnly
            ? activityByServers
                .filter((server: ActivityByServer): boolean =>
                    server.hasIssues
                )
            : activityByServers;
    }
);

export const getBckpServersHeatHeatmapFooter = createSelector(
    getServersHeatIssuesOnly(),
    (servers: ActivityByServer[]): ActivityByServer =>
    {
        if (servers.length === 0)
        {
            return null;
        }

        const activityByTotal = new ActivityByServer('Total');
        servers.forEach((activityByServer: ActivityByServer, idx: number): void =>
        {
            if (idx === 0)
            {
                activityByServer.details
                    .filter((activityByDate: ActivityByDate): boolean =>
                        !activityByDate.isServerTotal
                    )
                    .forEach((activityByDate: ActivityByDate): void =>
                    {
                        activityByTotal.details.push({ ...activityByDate });
                    });
            }
            else
            {
                activityByServer.details
                    .filter((activityByDate: ActivityByDate): boolean =>
                        !activityByDate.isServerTotal
                    )
                    .forEach((activityByDate: ActivityByDate, idx2: number): void =>
                    {
                        activityByTotal.details[idx2].nbFailedJobs += activityByDate.nbFailedJobs;
                        activityByTotal.details[idx2].nbPartiallyJobs += activityByDate.nbPartiallyJobs;
                        activityByTotal.details[idx2].nbFullyJobs += activityByDate.nbFullyJobs;
                        activityByTotal.details[idx2].nbJobs += activityByDate.nbJobs;
                    });
            }
        });

        activityByTotal.details.forEach((activityByDate: ActivityByDate): void =>
        {
            activityByDate.successRate = BckpHeatConstants.computeSuccessRate(activityByDate.nbJobs, activityByDate.nbFullyJobs);
            activityByDate.isGrandTotal = true;
        }
        );

        return activityByTotal;
    }
);
export const getBckpServersHeatTotalFooter = createSelector(
    getBckpServersHeatHeatmapFooter,
    (activityByServer: ActivityByServer): ActivityByServer =>
    {
        const activityByTotal = new ActivityByServer('GrandTotal');

        if (activityByServer !== null)
        {
            activityByTotal.details = [ActivityByServerDomain.computeSummary('', '', activityByServer.details)];
        }

        return activityByTotal;
    }
);
export const getServersHeatJobsQs = createSelector(
    getBckpServersHeatState,
    (state: fromServersHeatState.IBckpServersHeatState) =>
        state.jobsQs
);
const getBckpServersHeatJobsCollection = createSelector(
    getBckpServersHeatState,
    (state: fromServersHeatState.IBckpServersHeatState) =>
        state.jobs
);
export const {
    selectAll: getServersHeatJobs
} = jobsAdapter.getSelectors(getBckpServersHeatJobsCollection);
export const getServersHeatJobsNbLoaded = createSelector(
    getBckpServersHeatState,
    (state: fromServersHeatState.IBckpServersHeatState) =>
        state.nbJobs
);

export const getBckpServersHeatExportQueryString = createSelector(
    getBckpServersHeatState,
    (state: fromServersHeatState.IBckpServersHeatState) =>
        state.oData.qsExport
);
export const getAllActivitiesByIdentifier = createSelector(
    getAllServersHeat,
    (activities: ActivityByServer[], params: { identifier: string; }) =>
        activities.filter((activity: ActivityByServer) => activity.assetName() === params.identifier)
);
